import { useCallback, useEffect, useMemo, useState } from 'react'
import Attachments from '../components/Attachments.jsx'
import AdaptiveListDetail from '../components/AdaptiveListDetail.jsx'
import Sheet from '../components/Sheet.jsx'
import AccountIcon, { IconPicker, loadLogos } from '../components/AccountIcon.jsx'
import Tags from '../components/Tags.jsx'
import { addLink, linkCandidates } from '../lib/notes.js'
import {
  TYPES, addAccount, archiveAccount, getAccounts, getAdjustments, getSnapshots, pushedSince,
  moveBalance, moveLabels,
  isDebt, setBalance, signOf, snapshot, unarchiveAccount, updateAccount,
} from '../lib/worth.js'
import { parseAmount, peso, shortDate } from '../lib/format.js'

const LABEL = Object.fromEntries(TYPES.map((t) => [t.key, t.label]))

/** A line through the snapshots. Plain SVG — no library for one sparkline. */
function Line({ points }) {
  if (points.length < 2) return null
  const w = 300
  const h = 54
  const vals = points.map((p) => Number(p.total || 0))
  const lo = Math.min(...vals)
  const hi = Math.max(...vals)
  const span = hi - lo || 1
  const x = (i) => (i / (points.length - 1)) * w
  const y = (v) => h - ((v - lo) / span) * (h - 6) - 3

  const d = vals.map((v, i) => (i ? 'L' : 'M') + x(i).toFixed(1) + ' ' + y(v).toFixed(1)).join(' ')
  const area = d + ' L' + w + ' ' + h + ' L0 ' + h + ' Z'

  return (
    <svg className="spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      <path d={area} fill="var(--ocean-soft)" />
      <path d={d} fill="none" stroke="var(--ocean)" strokeWidth="1.6"
        strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      <circle cx={x(points.length - 1)} cy={y(vals[vals.length - 1])} r="2.6" fill="var(--ocean)" />
    </svg>
  )
}

/**
 * Every place money sits, with a balance he types. Nothing else in the app
 * ever writes here.
 *
 * Type decides the sign, so a credit card balance of 40,000 counts against the
 * total without him ever typing a minus.
 */
export default function NetWorth({ toast, jump = null, onJumpHandled }) {
  const [accounts, setAccounts] = useState(null)
  const [archived, setArchived] = useState([])
  const [points, setPoints] = useState([])
  const [error, setError] = useState(null)
  const [sheet, setSheet] = useState(null)
  const [selectedAccountId, setSelectedAccountId] = useState(null)
  const [showArchived, setShowArchived] = useState(false)
  const [logos, setLogos] = useState({})

  const load = useCallback(async () => {
    setError(null)
    try {
      const [all, snaps] = await Promise.all([getAccounts(true), getSnapshots()])
      setAccounts(all.filter((a) => !a.archived_at))
      setArchived(all.filter((a) => a.archived_at))
      setPoints(snaps)
      /* Signed URLs expire, so they are fetched with the list rather than
         stored. One round trip for every logo on the page. */
      loadLogos(all.map((a) => a.id)).then(setLogos).catch(() => {})
    } catch (e) {
      setError(e.message || String(e))
    }
  }, [])

  useEffect(() => { load() }, [load])

  const { total, assets, debts, grouped } = useMemo(() => {
    const live = (accounts ?? []).filter((a) => a.include_in_net_worth)
    let assets = 0
    let debts = 0
    for (const a of live) {
      const v = Number(a.balance || 0)
      if (isDebt(a.type)) debts += v
      else assets += v
    }
    const grouped = TYPES.map((t) => ({
      ...t,
      rows: (accounts ?? []).filter((a) => a.type === t.key),
    })).filter((g) => g.rows.length)

    return { total: assets - debts, assets, debts, grouped }
  }, [accounts])

  /* One snapshot a day, taken quietly after the accounts are read, so the
     line fills in without him ever thinking about it. */
  useEffect(() => {
    if (!accounts?.length) return
    const today = new Date().toISOString().slice(0, 10)
    if (points.some((p) => p.taken_on === today)) return
    snapshot(accounts).catch(() => {})
  }, [accounts, points])

  /* Arriving from a Log line. Wait for the accounts to load, then open the one
     it named and tell the shell the jump is spent, so going back and forward
     again does not re-open it. */
  useEffect(() => {
    if (!jump || jump.type !== 'account' || !accounts) return
    if (accounts.some((account) => account.id === jump.id)) setSelectedAccountId(jump.id)
    onJumpHandled?.()
  }, [jump, accounts, onJumpHandled])

  if (error) {
    return (
      <div className="notice warn">
        <p style={{ margin: 0, fontSize: 14 }}>Could not read your accounts. {error}</p>
      </div>
    )
  }
  if (!accounts) return <p className="lbl" style={{ padding: '20px 2px' }}>Loading…</p>

  const selectedAccount = accounts.find((account) => account.id === selectedAccountId) ?? null
  const addAccountAction = (
    <button type="button" className="btn ghost split-new-action"
      onClick={() => setSheet({ kind: 'new' })}>
      Add an account
    </button>
  )

  return (
    <>
      <AdaptiveListDetail
        storageKey="mypersonalos.split.netWorthWidth"
        listLabel="Accounts"
        selectedId={selectedAccount?.id ?? null}
        emptyLabel="Select an account"
        toolbarAction={addAccountAction}
        list={(
          <>
            <div className="card">
              <div className="pad">
                <span className="lbl">Net worth</span>
                <p className={'nw-total serif num' + (total < 0 ? ' neg' : '')}>{peso(total)}</p>
                {accounts.length ? (
                  <div className="split-key" style={{ marginTop: 6 }}>
                    <span><i style={{ background: 'var(--health)' }} />{peso(assets)} in</span>
                    {debts > 0 ? (
                      <span><i style={{ background: 'var(--late)' }} />{peso(debts)} owed</span>
                    ) : null}
                  </div>
                ) : null}
              </div>
              {points.length > 1 ? (
                <div className="spark-wrap">
                  <Line points={points} />
                  <div className="spark-ends lbl">
                    <span>{shortDate(points[0].taken_on)}</span>
                    <span>{shortDate(points[points.length - 1].taken_on)}</span>
                  </div>
                </div>
              ) : null}
            </div>

            {accounts.length === 0 ? (
              <div className="empty">
                <h2 className="serif">No accounts yet</h2>
                <p>Banks, online banks, e-wallets, cards, loans, investments. Add each one with
                  what is in it today, then log what goes in and out.</p>
              </div>
            ) : null}

            {grouped.map((g) => (
              <div key={g.key}>
                <p className="lbl" style={{ margin: '20px 2px 8px' }}>{g.label}</p>
                <div className="card">
                  <div className="rows">
                    {g.rows.map((a) => (
                      <button
                        className={'row account-row' + (selectedAccount?.id === a.id ? ' selected' : '')}
                        key={a.id}
                        type="button"
                        data-detail-id={a.id}
                        aria-current={selectedAccount?.id === a.id ? 'true' : undefined}
                        onClick={() => setSelectedAccountId(a.id)}
                      >
                        <AccountIcon account={a} logo={logos[a.id]} />
                        <div className="nm">
                          <b>{a.name}</b>
                          <span className="lbl">
                            {a.institution || LABEL[a.type]}
                            {a.include_in_net_worth ? '' : ' · not counted'}
                          </span>
                        </div>
                        <span className={'amt num' + (signOf(a.type) < 0 ? ' owed' : '')}>
                          {signOf(a.type) < 0 ? '−' : ''}{peso(a.balance)}
                        </span>
                        <span className="go" aria-hidden="true">›</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}

            {archived.length ? (
              <>
                <button type="button" className="linkish" style={{ marginTop: 16 }}
                  onClick={() => setShowArchived((v) => !v)}>
                  {showArchived ? 'Hide' : 'Show'} {archived.length} archived
                </button>
                {showArchived ? (
                  <div className="card" style={{ marginTop: 8, opacity: .72 }}>
                    <div className="rows">
                      {archived.map((a) => (
                        <div className="row" key={a.id}>
                          <div className="nm">
                            <b>{a.name}</b>
                            <span className="lbl">{LABEL[a.type]} · archived</span>
                          </div>
                          <button type="button" className="linkish" onClick={async () => {
                            try {
                              await unarchiveAccount(a)
                              load()
                            } catch (e) {
                              toast(e.message || 'Could not restore that.')
                            }
                          }}>Restore</button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </>
            ) : null}
          </>
        )}
        detail={selectedAccount ? (
          <BalanceSheet
            key={selectedAccount.id}
            account={selectedAccount}
            logo={logos[selectedAccount.id]}
            onClose={() => setSelectedAccountId(null)}
            onSaved={() => { setSelectedAccountId(null); load() }}
            toast={toast}
          />
        ) : null}
      />

      {sheet?.kind === 'new' ? (
        <AccountSheet
          onClose={() => setSheet(null)}
          onSaved={() => { setSheet(null); load() }}
          toast={toast}
        />
      ) : null}
    </>
  )
}

function AccountSheet({ onClose, onSaved, toast }) {
  const [f, setF] = useState({ name: '', institution: '', type: 'savings', balance: '' })
  const [busy, setBusy] = useState(false)
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }))

  return (
    <Sheet title="Add an account" onClose={onClose}>
      <form
        onSubmit={async (e) => {
          e.preventDefault()
          if (busy || !f.name.trim()) return
          setBusy(true)
          try {
            await addAccount({
              name: f.name,
              institution: f.institution,
              type: f.type,
              balance: parseAmount(f.balance) || 0,
            })
            onSaved()
          } catch (err) {
            toast(err.message || 'Could not save that.')
          } finally {
            setBusy(false)
          }
        }}
      >
        <div className="field">
          <label className="lbl" htmlFor="a-name">Name</label>
          <input id="a-name" required value={f.name} onChange={(e) => set('name', e.target.value)} />
        </div>
        <div className="field">
          <label className="lbl" htmlFor="a-inst">Institution</label>
          <input id="a-inst" placeholder="BPI, GCash, Maya…"
            value={f.institution} onChange={(e) => set('institution', e.target.value)} />
        </div>
        <div className="field">
          <label className="lbl" htmlFor="a-type">Type</label>
          <select id="a-type" value={f.type} onChange={(e) => set('type', e.target.value)}>
            {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </div>
        <div className="field">
          <label className="lbl" htmlFor="a-bal">
            {isDebt(f.type) ? 'Amount owed' : 'Balance'}
          </label>
          <input id="a-bal" inputMode="numeric" placeholder="0"
            value={f.balance} onChange={(e) => set('balance', e.target.value)} />
          {isDebt(f.type) ? (
            <p className="lbl" style={{ margin: '6px 0 0' }}>
              Counts against your total. No minus sign needed.
            </p>
          ) : null}
        </div>
        <button className="btn" type="submit" data-sheet-primary disabled={busy || !f.name.trim()}>
          {busy ? 'Saving…' : 'Add it'}
        </button>
      </form>
    </Sheet>
  )
}

/*
 * One account, opened.
 *
 * Money in and money out are the two buttons, because that is what actually
 * happens to an account and it is the only version that leaves a history
 * worth reading. Typing a new balance outright is still here, one level down,
 * for the day the bank app and this disagree — and it is recorded as a
 * correction so the two never look alike in the list of changes.
 */
function BalanceSheet({ account, logo, onClose, onSaved, toast }) {
  const words = moveLabels(account.type)
  const [dir, setDir] = useState('up')
  const [amt, setAmt] = useState('')
  const [reason, setReason] = useState('')
  const [history, setHistory] = useState([])
  const [busy, setBusy] = useState(false)
  const [correcting, setCorrecting] = useState(false)
  const [v, setV] = useState(String(Math.round(Number(account.balance || 0))))
  /* What the money is for. Offered only on the way out, because a deposit is
     saving toward something and that is what a Build already is. */
  const [forOpts, setForOpts] = useState([])
  const [forKey, setForKey] = useState('')

  useEffect(() => {
    let alive = true
    Promise.all([linkCandidates('project'), linkCandidates('build')])
      .then(([projects, builds]) => {
        if (!alive) return
        setForOpts([
          ...projects.map((p) => ({ ...p, type: 'project', group: 'Project' })),
          ...builds.map((b) => ({ ...b, type: 'build', group: 'Build' })),
        ])
      })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  useEffect(() => {
    getAdjustments(account.id).then(setHistory).catch(() => {})
  }, [account.id])

  /* What arrived from Cashflow since he last set this by hand. Shown so the
     gap between this number and the bank's is visible, not silent. */
  const pushed = pushedSince(history)

  const preview = (() => {
    const n = parseAmount(amt) || 0
    if (!n) return null
    const old = Number(account.balance || 0)
    return dir === 'down' ? old - n : old + n
  })()

  return (
    <Sheet title={account.name} onClose={onClose}>
      <div style={{ marginBottom: 16 }}>
        <span className="lbl">{isDebt(account.type) ? 'Owed now' : 'Balance now'}</span>
        <p className="num" style={{ margin: '2px 0 0', fontSize: 26, fontWeight: 600 }}>
          {peso(account.balance)}
        </p>
      </div>

      {pushed ? (
        <div className="notice" style={{ margin: '0 0 14px' }}>
          <p style={{ margin: 0, fontSize: 13 }}>
            {peso(pushed)} has been pushed in since you last set this by hand. That is
            already counted above. Worth checking it against the bank.
          </p>
        </div>
      ) : null}

      <form
        onSubmit={async (e) => {
          e.preventDefault()
          if (busy) return
          setBusy(true)
          try {
            const { adjustment } = await moveBalance(account, dir, parseAmount(amt), reason)
            /* The link is best-effort. The money has already moved, and
               failing the whole thing over a cross-reference would leave him
               wondering whether the withdrawal went through. */
            if (dir === 'down' && forKey && adjustment?.id) {
              const [type, id] = forKey.split(':')
              try {
                await addLink({ type: 'account_move', id: adjustment.id }, { type, id })
              } catch {
                toast('Money moved, but it could not be tagged to that.')
              }
            }
            onSaved()
          } catch (err) {
            toast(err.message || 'Could not save that.')
            setBusy(false)
          }
        }}
      >
        <div className="seg" style={{ marginBottom: 12 }}>
          <button type="button" className={dir === 'up' ? 'on' : ''} onClick={() => setDir('up')}>
            {words.up}
          </button>
          <button type="button" className={dir === 'down' ? 'on' : ''} onClick={() => setDir('down')}>
            {words.down}
          </button>
        </div>

        <div className="field">
          <label className="lbl" htmlFor="mv-amt">
            {dir === 'up' ? words.upHint : words.downHint}
          </label>
          <input id="mv-amt" inputMode="numeric" autoFocus placeholder="0"
            value={amt} onChange={(e) => setAmt(e.target.value)} />
        </div>

        {preview !== null ? (
          <p className="lbl" style={{ margin: '-6px 2px 12px' }}>
            {peso(account.balance)} → <b className="num">{peso(preview)}</b>
          </p>
        ) : null}

        <div className="field">
          <label className="lbl" htmlFor="mv-why">
            {correcting ? 'Why does this need correcting?' : 'What for'}
          </label>
          <input id="mv-why" required placeholder="Required"
            value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>

        {dir === 'down' && forOpts.length ? (
          <div className="field">
            <label className="lbl" htmlFor="mv-for">Counts toward</label>
            <select id="mv-for" value={forKey} onChange={(e) => setForKey(e.target.value)}>
              <option value="">Nothing in particular</option>
              {forOpts.map((o) => (
                <option key={o.type + ':' + o.id} value={o.type + ':' + o.id}>
                  {o.group}: {o.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <button className="btn" type="submit" data-sheet-primary disabled={busy || !parseAmount(amt)}>
          {busy ? 'Saving…' : 'Log ' + (dir === 'up' ? words.up : words.down).toLowerCase()}
        </button>
      </form>

      <button type="button" className="linkish" style={{ marginTop: 12 }}
        onClick={() => setCorrecting((c) => !c)}>
        {correcting ? 'Never mind' : 'Correct the balance instead'}
      </button>

      {correcting ? (
        <form
          style={{ marginTop: 10 }}
          onSubmit={async (e) => {
            e.preventDefault()
            if (busy) return
            setBusy(true)
            try {
              await setBalance(account, parseAmount(v) || 0, reason)
              onSaved()
            } catch (err) {
              toast(err.message || 'Could not save that.')
              setBusy(false)
            }
          }}
        >
          <div className="field">
            <label className="lbl" htmlFor="b-val">
              {isDebt(account.type) ? 'Amount owed, as the bank has it' : 'Balance, as the bank has it'}
            </label>
            <input id="b-val" inputMode="numeric" value={v} onChange={(e) => setV(e.target.value)} />
          </div>
          <button className="btn ghost" type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save correction'}
          </button>
        </form>
      ) : null}

      <div style={{ marginTop: 20 }}>
        <IconPicker account={account} logo={logo} onChanged={onSaved} toast={toast} />
      </div>

      <Tags target={{ type: 'account', id: account.id }} toast={toast} />
      {/* The screenshot behind a figure: a transfer receipt, a balance page. */}
      <Attachments target={{ type: 'account', id: account.id }} toast={toast} compact />

      <div className="sheet-actions">
        <button type="button" className="btn ghost" onClick={async () => {
          try {
            await updateAccount(account.id, { include_in_net_worth: !account.include_in_net_worth })
            onSaved()
          } catch (e) {
            toast(e.message || 'Could not update that.')
          }
        }}>
          {account.include_in_net_worth ? 'Stop counting it' : 'Count it again'}
        </button>
        <button type="button" className="btn ghost" onClick={async () => {
          try {
            await archiveAccount(account)
            onSaved()
          } catch (e) {
            toast(e.message || 'Could not archive that.')
          }
        }}>
          Archive
        </button>
      </div>

      {history.length ? (
        <>
          <p className="lbl" style={{ margin: '22px 0 6px' }}>Changes</p>
          {history.map((h) => {
            const delta = Number(h.new_balance || 0) - Number(h.old_balance || 0)
            return (
              <div className="adjrow" key={h.id}>
                <span className="lbl">{shortDate(h.changed_on)}</span>
                {/* A movement reads as the movement. A correction has no
                    meaningful delta, so it reads as the jump it was. */}
                {h.kind === 'correction' || !h.kind ? (
                  <span className="num">{peso(h.old_balance)} → {peso(h.new_balance)}</span>
                ) : (
                  <span className={'num' + (delta < 0 ? ' owed' : '')}>
                    {delta < 0 ? '−' : '+'}{peso(Math.abs(delta))}
                  </span>
                )}
                {h.source_payable_id ? (
                  <em className="frompush">pushed{h.reason ? ' · ' + h.reason : ''}</em>
                ) : h.reason ? <em>{h.reason}</em> : h.kind ? <em>{h.kind}</em> : null}
              </div>
            )
          })}
        </>
      ) : null}
    </Sheet>
  )
}
